import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getRoleLandingPath } from '@/lib/roleLanding';

/**
 * Role-based route protection component
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to render if authorized
 * @param {string[]} [props.allowedRoles] - Array of roles that can access this route (e.g., ['owner', 'admin'])
 * @param {string} [props.pagePermission] - page_permissions key required for roles in pagePermissionRoles
 * @param {string[]} [props.pagePermissionRoles] - Roles that must also have pagePermission
 */
const ProtectedRoute = ({ children, allowedRoles = null, pagePermission = null, pagePermissionRoles = null, redirectTo = null }) => {
  const { user, loading, token, sessionModalOpen } = useAuth();
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  if (token && !user && sessionModalOpen) {
    return children;
  }
  
  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check role-based access if allowedRoles is specified
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = user.role || 'staff';
    
    if (!allowedRoles.includes(userRole)) {
      return <Navigate to={redirectTo || getRoleLandingPath(userRole)} replace />;
    }
    if (pagePermission && pagePermissionRoles?.includes(userRole) && !user?.page_permissions?.[pagePermission]) {
      return <Navigate to={redirectTo || getRoleLandingPath(userRole)} replace />;
    }
  }
  
  // User is authenticated and authorized
  return children;
};

export default ProtectedRoute;
